import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { withStyles } from "@material-ui/core/styles";
import { Typography, WithStyles } from "@material-ui/core";
import FileCopyIcon from "@material-ui/icons/FileCopy";
import { styles } from "./ManageSubscriptionsView.styles";
import QuotaModel from "../../../models/quota/QuotaModel";
import * as QuotaAction from "../../../stores/quotas/QuotaAction";
import UniversalTable from "../../components/UniversalTable/UniversalTable";
import {
    IUniversalTableColumn,
    IUniversalTableData,
    IUniversalTableDetailPanelColumn
} from "../../components/UniversalTable/interfaces";
import { useSnackbar } from "notistack";
import ToastStatusEnum from "../../../constants/ToastStatusEnum";
import { useTranslation } from "react-i18next";
import { selectRequesting } from "../../../selectors/requesting/RequestingSelector";
import { Breadcrumbs } from "../../components/Breadcrumbs/Breadcrumbs";
import { useHistory } from "react-router-dom";
import { CenteredContainer } from "../../components/CenteredContainer/CenteredContainer";
import { consolidateDateToNumber } from "../../../utilities/dateTimeUtils";
import { areQuotasExclusive, IQuota, isQuotaValid } from "../../../utilities/quotaUtils";

export interface IManageQuotasViewProps extends WithStyles<typeof styles> {}

const ManageQuotasViewPure: React.FunctionComponent<IManageQuotasViewProps> = ({ classes }: IManageQuotasViewProps) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();
    const history = useHistory();
    const { t } = useTranslation();

    useEffect(() => {
        dispatch(QuotaAction.requestAllQuotas());
    }, [dispatch]);

    const quotasFromState = useSelector(state => state.quotas);
    const isRequesting = useSelector(state => selectRequesting(state, [QuotaAction.REQUEST_ALL_QUOTAS]));

    let quotas: IUniversalTableData;
    if (quotasFromState?.allQuotas) {
        quotas = Object.values(quotasFromState?.allQuotas)?.map((quota: QuotaModel) => ({
            id: quota.id,
            totalAmount: quota.totalAmount,
            usedAmount: quota.usedAmount,
            remainingAmount: quota.remainingAmount,
            dateFrom: quota.dateFrom,
            dateTo: quota.dateTo,
            comment: quota.comment,
            created: quota.created
        }));
    }

    const handleQuotaOrders = index => {
        const path = "/admin/quotas/" + quotas[index].id + "/orders";
        history.push(path);
    };

    const handleEditFinished = (index, newData) => {
        const updatedQuota = {
            dateFrom: consolidateDateToNumber(newData.dateFrom),
            dateTo: consolidateDateToNumber(newData.dateTo),
            totalAmount: parseInt(newData.totalAmount),
            comment: newData.comment
        }
        dispatch(QuotaAction.updateQuota(quotas[index].id, updatedQuota))
            .then(() => {
                dispatch(QuotaAction.requestAllQuotas());
                enqueueSnackbar("Quota edited", {
                    variant: ToastStatusEnum.Info
                });
            })
            .catch(error =>
                enqueueSnackbar("Quota editing fialed: " + error, {
                    variant: ToastStatusEnum.Error
                })
            );
    };

    const handleDeleteFinished = index => {
        dispatch(QuotaAction.deleteQuote(quotas[index].id))
            .then(() => {
                dispatch(QuotaAction.requestAllQuotas());
                enqueueSnackbar("Quota deleted", {
                    variant: ToastStatusEnum.Info
                });
            })
            .catch(error =>
                enqueueSnackbar("Quota deletion failed: " + error, {
                    variant: ToastStatusEnum.Error
                })
            );
    };

    const handleAddFinished = data => {
        const newQuota = {
            dateFrom: consolidateDateToNumber(data.dateFrom),
            dateTo: consolidateDateToNumber(data.dateTo),
            totalAmount: parseInt(data.totalAmount),
            comment: data.comment
        }
        dispatch(QuotaAction.addQuota(newQuota))
            .then(() => {
                dispatch(QuotaAction.requestAllQuotas());
                enqueueSnackbar("Quota added", {
                    variant: ToastStatusEnum.Info
                });
            })
            .catch(error =>
                enqueueSnackbar("Quota addition failed: " + error, {
                    variant: ToastStatusEnum.Error
                })
            );
    };

    const columns: IUniversalTableColumn[] = [
        {
            type: "price",
            title: "Total Order Quota",
            field: "totalAmount",
            defaultValue: 1000000,
            min: 1,
            validate: rowData => {
                return String(rowData?.totalAmount)?.length === 0 || rowData?.totalAmount <= 0
                    ? { isValid: false, helperText: "Quota must be higher than zero" }
                    : rowData?.totalAmount < rowData?.usedAmount
                    ? { isValid: false, helperText: "Quota cannot be lower than already used amount" }
                    : true;
            }
        },
        {
            type: "date",
            title: "Start Date",
            field: "dateFrom",
            validate: rowData => {
                const quotasLocal: IQuota[] = quotas.map(q => ({ from: q.dateFrom, to: q.dateTo } as IQuota));
                if (rowData?.tableData?.editing) quotasLocal.splice(rowData?.tableData?.id, 1);
                const newQuota = {
                    from: consolidateDateToNumber(rowData.dateFrom),
                    to: consolidateDateToNumber(rowData.dateTo)
                };
                if (!isQuotaValid(newQuota))
                    return { isValid: false, helperText: "Start date must be before end date" };
                return !areQuotasExclusive(quotasLocal, newQuota)
                    ? { isValid: false, helperText: "Quotas cannot overlap" }
                    : true;
            }
        },
        {
            type: "date",
            title: "End Date",
            field: "dateTo",
            validate: rowData => {
                const quotasLocal: IQuota[] = quotas.map(q => ({ from: q.dateFrom, to: q.dateTo } as IQuota));
                if (rowData?.tableData?.editing) quotasLocal.splice(rowData?.tableData?.id, 1);
                const newQuota = {
                    from: consolidateDateToNumber(rowData.dateFrom),
                    to: consolidateDateToNumber(rowData.dateTo)
                };
                if (!isQuotaValid(newQuota))
                    return { isValid: false, helperText: "Start date must be before end date." };
                return !areQuotasExclusive(quotasLocal, newQuota)
                    ? { isValid: false, helperText: "Quotas cannot overlap." }
                    : true;
            }
        },
        {
            type: "text",
            title: "Comment",
            field: "comment"
        },
        {
            type: "actions",
            title: t("admin.taskView.actions"),
            actions: [
                {
                    name: "manageOrders",
                    icon: <FileCopyIcon />,
                    tooltip: "Quota Orders",
                    onClick: index => handleQuotaOrders(index),
                    scope: ["orders.quota.read", "orders.quota.write"]
                },
                {
                    name: "edit",
                    tooltip: t("general.edit"),
                    onEditFinished: (index, newValue) => handleEditFinished(index, newValue),
                    scope: "orders.quota.write"
                },
                {
                    name: "delete",
                    tooltip: t("general.delete"),
                    onDeleteFinished: index => handleDeleteFinished(index),
                    deleteQuestion: "Do you want to delete this quota?",
                    scope: "orders.quota.write"

                },
                {
                    name: "add",
                    tooltip: "Add quota",
                    onAddFinished: newValue => handleAddFinished(newValue),
                    scope: "orders.quota.write"
                }
            ]
        }
    ];

    const detailPanelTableColumns: IUniversalTableDetailPanelColumn[] = [
        {
            title: "Used Quota",
            field: "usedAmount",
            type: "price"
        },
        {
            title: "Remaining Quota",
            field: "remainingAmount",
            type: "price"
        },
        {
            title: "Created",
            field: "created",
            type: "dateTime"
        }
    ];

    return (
        <CenteredContainer>
            <Breadcrumbs items={[{ title: t("admin.adminPanel"), href: "/admin" }, { title: "Manage Quotas" }]} />
            <div className={classes.title}>
                <Typography variant="h4">Manage Quotas</Typography>
            </div>
            <UniversalTable
                columns={columns}
                data={quotas}
                detailPanelTableColumns={detailPanelTableColumns}
                isLoading={isRequesting}
                searchable
            />
        </CenteredContainer>
    );
};

export const ManageQuotasView = withStyles(styles)(ManageQuotasViewPure);
